import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getSuppliers, createSupplier, updateSupplier, getSupplierHistory } from '../utils/api';

const TRADES = [
  'Structural Steel','Concrete','Masonry','Façade','Roofing',
  'Electrical','HVAC','Fire Protection','Groundworks','Finishes',
  'Plumbing','MEP','Landscaping','Civils','Other'
];

const TRADE_COLORS = {
  'Structural Steel': { color: 'var(--color-info)',    bg: '#EFF6FF' },
  'Concrete':         { color: '#7C3AED',              bg: '#F5F3FF' },
  'Masonry':          { color: '#92400E',              bg: '#FFFBEB' },
  'Façade':           { color: '#0E7490',              bg: '#ECFEFF' },
  'Roofing':          { color: '#5B21B6',              bg: '#EDE9FE' },
  'Electrical':       { color: 'var(--color-warning)', bg: '#FFFBEB' },
  'HVAC':             { color: 'var(--color-teal)',    bg: 'var(--color-teal-light)' },
  'Fire Protection':  { color: 'var(--color-error)',   bg: '#FEF2F2' },
  'Groundworks':      { color: 'var(--color-info)',    bg: '#EFF6FF' },
  'Finishes':         { color: '#6D28D9',              bg: '#EDE9FE' },
  'Plumbing':         { color: '#0369A1',              bg: '#F0F9FF' },
  'MEP':              { color: '#0D9488',              bg: '#CCFBF1' },
  'Other':            { color: 'var(--color-text-mid)',bg: 'var(--color-background)' },
};

function SupplierDrawer({ supplier, onClose, onUpdate }) {
  const [history, setHistory] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (supplier) {
      setForm({ ...supplier });
      loadHistory();
    }
  }, [supplier]);

  async function loadHistory() {
    try {
      const data = await getSupplierHistory(supplier.id);
      setHistory(data);
    } catch (err) { console.error(err); }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateSupplier(supplier.id, form);
      onUpdate();
      setEditing(false);
    } catch (err) { console.error(err); }
    setSaving(false);
  }

  if (!supplier) return null;
  const trade = supplier.trade_categories?.[0] || 'Other';
  const tc = TRADE_COLORS[trade] || TRADE_COLORS['Other'];

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: tc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: tc.color, flexShrink: 0 }}>
              {supplier.name[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>{supplier.name}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                {supplier.trade_categories?.map(t => (
                  <span key={t} style={{ padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600, color: (TRADE_COLORS[t] || TRADE_COLORS['Other']).color, background: (TRADE_COLORS[t] || TRADE_COLORS['Other']).bg }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setEditing(!editing)} className="btn btn-secondary btn-sm">
              {editing ? 'Cancel' : '✎ Edit'}
            </button>
            <button onClick={onClose} className="btn btn-ghost btn-sm">✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs" style={{ padding: '0 24px' }}>
          {['overview', 'history', 'performance'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={`tab-btn ${activeTab === t ? 'active' : ''}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        <div className="drawer-body">

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {editing && form ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Company Name</label>
                    <input className="form-input" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Email</label>
                    <input className="form-input" type="email" value={form.contact_email || ''} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Region</label>
                    <input className="form-input" value={form.region || ''} onChange={e => setForm({ ...form, region: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={form.approved_status || 'approved'} onChange={e => setForm({ ...form, approved_status: e.target.value })}>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="blacklisted">Blacklisted</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea className="form-textarea" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Team notes about this supplier..." />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleSave} disabled={saving} className="btn btn-primary btn-sm">{saving ? 'Saving...' : 'Save Changes'}</button>
                    <button onClick={() => setEditing(false)} className="btn btn-secondary btn-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Contact info */}
                  <div>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Contact</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        ['Email', supplier.contact_email || '—'],
                        ['Phone', supplier.phone || '—'],
                        ['Region', supplier.region || '—'],
                        ['Contact Name', supplier.contact_name || '—'],
                      ].map(([l, v]) => (
                        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-light)', fontWeight: 500 }}>{l}</span>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-dark)', fontWeight: 500 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Status</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span className={`badge ${supplier.approved_status === 'approved' ? 'badge-success' : supplier.approved_status === 'blacklisted' ? 'badge-error' : 'badge-warning'}`}>
                        {supplier.approved_status || 'pending'}
                      </span>
                    </div>
                  </div>

                  {/* Notes */}
                  {supplier.notes && (
                    <div>
                      <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Team Notes</div>
                      <div style={{ padding: '12px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--color-text-dark)' }}>
                        {supplier.notes}
                      </div>
                    </div>
                  )}

                  {/* Intelligence signals */}
                  <div>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Intelligence Signals</div>
                    <div className="grid-2">
                      {[
                        ['Response Rate', supplier.response_rate != null ? `${supplier.response_rate}%` : '—', supplier.response_rate >= 80 ? 'var(--color-success)' : 'var(--color-text-dark)'],
                        ['Total RFQs', supplier.total_rfqs || 0, 'var(--color-text-dark)'],
                        ['Total Quotes', supplier.total_quotes || 0, 'var(--color-text-dark)'],
                        ['Rating', supplier.rating ? `${supplier.rating}/5` : '—', 'var(--color-warning)'],
                      ].map(([label, value, color]) => (
                        <div key={label} style={{ padding: '12px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 4 }}>{label}</div>
                          <div style={{ fontSize: '1.125rem', fontWeight: 700, color, fontFamily: 'var(--font-mono)' }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--color-teal-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-teal)', fontSize: '0.75rem', color: 'var(--color-teal)', fontWeight: 500 }}>
                      ✦ Intelligence signals are based on real procurement history — your team makes the final call
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Quote history */}
              <div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Quote History</div>
                {!history ? (
                  <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-light)', fontSize: '0.875rem' }}>Loading...</div>
                ) : history.quotes?.length === 0 ? (
                  <div style={{ padding: '14px 16px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-light)', textAlign: 'center' }}>
                    No quote history yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {history.quotes?.map(q => (
                      <div key={q.id} style={{ padding: '12px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{q.package_name}</span>
                          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-teal)' }}>
                            {q.total_price ? `£${Number(q.total_price).toLocaleString()}` : '—'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                          {q.project_name} · {new Date(q.created_at).toLocaleDateString()}
                          {q.lead_time_days && ` · ${q.lead_time_days} days lead`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* RFQ history */}
              <div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>RFQ History</div>
                {!history ? (
                  <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-light)', fontSize: '0.875rem' }}>Loading...</div>
                ) : history.rfqs?.length === 0 ? (
                  <div style={{ padding: '14px 16px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-light)', textAlign: 'center' }}>
                    No RFQ history yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {history.rfqs?.map(r => (
                      <div key={r.id} style={{ padding: '12px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{r.package_name}</span>
                          <span className={`badge ${r.responded_at ? 'badge-success' : 'badge-warning'}`}>
                            {r.responded_at ? 'Responded' : 'No response'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                          Sent: {r.sent_at ? new Date(r.sent_at).toLocaleDateString() : '—'}
                          {r.responded_at && ` · Responded: ${new Date(r.responded_at).toLocaleDateString()}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PERFORMANCE TAB */}
          {activeTab === 'performance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ padding: '16px', background: 'var(--color-teal-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-teal)' }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-teal)', marginBottom: 4 }}>✦ AI Performance Analysis</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-dark)', lineHeight: 1.6 }}>
                  Performance data builds automatically as you send RFQs and receive quotes. The more you use Procura, the richer this intelligence becomes.
                </div>
              </div>

              {/* Performance metrics */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'Response Rate', value: supplier.response_rate, suffix: '%', max: 100 },
                  { label: 'Quote Win Rate', value: null, suffix: '%', max: 100 },
                  { label: 'Price vs Market', value: null, suffix: '%', max: 100 },
                ].map(metric => (
                  <div key={metric.label} style={{ padding: '14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{metric.label}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-teal)' }}>
                        {metric.value != null ? `${metric.value}${metric.suffix}` : '—'}
                      </span>
                    </div>
                    <div style={{ height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: metric.value != null ? `${(metric.value / metric.max) * 100}%` : '0%', background: 'var(--color-teal)', borderRadius: 2, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Rating */}
              <div>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Team Rating</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <div key={star} style={{ fontSize: '1.5rem', cursor: 'pointer', color: supplier.rating >= star ? 'var(--color-warning)' : 'var(--color-border)' }}>★</div>
                  ))}
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-text-light)', alignSelf: 'center' }}>
                    {supplier.rating ? `${supplier.rating}/5` : 'Not rated'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function Suppliers() {
  const { addToast } = useOutletContext() || {};
  const [suppliers, setSuppliers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', contact_email: '', phone: '', trade_categories: ['Concrete'], region: '', contact_name: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [tradeFilter, setTradeFilter] = useState('All');
  const [drawerSupplier, setDrawerSupplier] = useState(null);

  useEffect(() => { loadSuppliers(); }, []);

  async function loadSuppliers() {
    setLoading(true);
    try {
      const data = await getSuppliers();
      setSuppliers(data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await createSupplier(form);
      setShowNew(false);
      setForm({ name: '', contact_email: '', phone: '', trade_categories: ['Concrete'], region: '', contact_name: '', notes: '' });
      setMessage({ type: 'success', text: 'Supplier added successfully' });
      loadSuppliers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to add supplier' });
    }
  }

  const filtered = suppliers.filter(s => {
    const trade = s.trade_categories?.[0] || '';
    const searchMatch = !search.trim() ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.contact_email || '').toLowerCase().includes(search.toLowerCase());
    const tradeMatch = tradeFilter === 'All' || trade === tradeFilter;
    return searchMatch && tradeMatch;
  });

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Suppliers</div>
          <div className="page-subtitle">{suppliers.length} suppliers in directory</div>
        </div>
        <div className="page-actions">
          <button onClick={() => setShowNew(true)} className="btn btn-primary">+ Add Supplier</button>
        </div>
      </div>

      <SupplierDrawer
        supplier={drawerSupplier}
        onClose={() => setDrawerSupplier(null)}
        onUpdate={() => { loadSuppliers(); setDrawerSupplier(null); }}
      />

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, position: 'relative', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-light)', fontSize: '1rem' }}>⌕</span>
          <input className="form-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." style={{ paddingLeft: 36 }} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['All', ...TRADES].map(t => (
            <button key={t} onClick={() => setTradeFilter(t)} className="btn btn-sm"
              style={{ background: tradeFilter === t ? 'var(--color-teal)' : 'var(--color-surface)', color: tradeFilter === t ? '#fff' : 'var(--color-text-mid)', border: `1px solid ${tradeFilter === t ? 'var(--color-teal)' : 'var(--color-border)'}` }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontWeight: 500, marginBottom: 16, background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)' }}>
          {message.text}
        </div>
      )}

      {/* New supplier form */}
      {showNew && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span style={{ fontWeight: 600 }}>Add New Supplier</span>
            <button onClick={() => setShowNew(false)} className="btn btn-ghost btn-sm">✕</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleCreate}>
              <div className="grid-2" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Company Name *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Supplier Ltd" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Email</label>
                  <input className="form-input" type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@supplier.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Name</label>
                  <input className="form-input" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="John Smith" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+44 20 7946 0100" />
                </div>
                <div className="form-group">
                  <label className="form-label">Trade Category</label>
                  <select className="form-select" value={form.trade_categories[0]} onChange={e => setForm({ ...form, trade_categories: [e.target.value] })}>
                    {TRADES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Region</label>
                  <input className="form-input" value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} placeholder="London, UK" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Team notes about this supplier..." />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary">Add Supplier</button>
                <button type="button" onClick={() => setShowNew(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Suppliers table */}
      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : suppliers.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <div className="empty-state-icon">◉</div>
          <h3>No suppliers yet</h3>
          <p>Add your first supplier to start sending RFQs</p>
          <button onClick={() => setShowNew(true)} className="btn btn-primary">+ Add Supplier</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state card" style={{ padding: 32 }}>
          <p>No suppliers match "{search}"</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <span style={{ fontWeight: 600 }}>Supplier Directory ({filtered.length})</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {['Supplier', 'Trade', 'Region', 'Contact', 'Response Rate', 'Quotes', 'Status', ''].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const trade = s.trade_categories?.[0] || 'Other';
                  const tc = TRADE_COLORS[trade] || TRADE_COLORS['Other'];
                  return (
                    <tr key={s.id} onClick={() => setDrawerSupplier(s)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: tc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: tc.color, flexShrink: 0 }}>
                            {s.name[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{s.name}</div>
                            {s.contact_name && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>{s.contact_name}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600, color: tc.color, background: tc.bg }}>
                          {trade}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{s.region || '—'}</td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{s.contact_email || '—'}</td>
                      <td>
                        {s.response_rate != null ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 50, height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${s.response_rate}%`, background: s.response_rate >= 80 ? 'var(--color-success)' : 'var(--color-warning)', borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: s.response_rate >= 80 ? 'var(--color-success)' : 'var(--color-warning)' }}>{s.response_rate}%</span>
                          </div>
                        ) : <span style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>—</span>}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{s.total_quotes || 0}</td>
                      <td>
                        <span className={`badge ${s.approved_status === 'approved' ? 'badge-success' : s.approved_status === 'blacklisted' ? 'badge-error' : 'badge-neutral'}`}>
                          {s.approved_status || 'pending'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--color-teal)', fontWeight: 500 }}>View →</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}